if (!customElements.get('cart-quantity-sync')) {
  const KKMCartQuantityCoordinator = window.KKMCartQuantityCoordinator || {
    pending: null,
    cart: null,
    quantityFor(cart, variantId) {
      return (cart?.items || [])
        .filter((item) => String(item.variant_id) === String(variantId))
        .reduce((sum, item) => sum + item.quantity, 0);
    },
    getNodes(root, selector) {
      const nodes = [];
      if (root instanceof Element && root.matches(selector)) nodes.push(root);
      if (root?.querySelectorAll) nodes.push(...root.querySelectorAll(selector));
      return nodes;
    },
    apply(cart, root = document) {
      if (!cart?.items) return;
      this.cart = cart;

      const quantities = cart.items.reduce((map, item) => {
        const key = String(item.variant_id);
        map[key] = (map[key] || 0) + item.quantity;
        return map;
      }, {});

      this.getNodes(root, 'input[data-quantity-variant-id]').forEach((input) => {
        const variantId = String(input.dataset.quantityVariantId || '');
        if (!variantId) return;
        const quantity = quantities[variantId] || 0;
        input.value = quantity;
        input.setAttribute('value', quantity);
        input.dataset.cartQuantity = quantity;

        const quickAdd = input.closest('quick-add-bulk');
        if (quickAdd) {
          quickAdd.querySelector('.qv_cart_btn')?.classList.toggle('hidden', quantity > 0);
          quickAdd.querySelector('.qv_add_input')?.classList.toggle('hidden', quantity === 0);
        }
      });

      this.getNodes(root, '[data-kkm-search-row]').forEach((row) => {
        const variantId = String(row.dataset.variantId || '');
        if (!variantId) return;
        const quantity = quantities[variantId] || 0;
        const addWrap = row.querySelector('[data-kkm-add-wrap]');
        const qtyWrap = row.querySelector('[data-kkm-qty]');
        const qtyValue = row.querySelector('[data-kkm-qty-value]');
        if (!addWrap || !qtyWrap || !qtyValue) return;

        const hadFocus = row.contains(document.activeElement);
        qtyValue.textContent = quantity;
        addWrap.hidden = quantity > 0;
        qtyWrap.hidden = quantity === 0;

        if (hadFocus) {
          const nextFocus = quantity > 0
            ? row.querySelector('[data-kkm-qty-action="plus"]')
            : row.querySelector('[data-kkm-add]');
          requestAnimationFrame(() => nextFocus?.focus({ preventScroll: true }));
        }
      });

      this.getNodes(root, 'cart-quantity-sync').forEach((control) => control.syncFromCart(cart));
    },
    syncRoot(root = document) {
      if (this.cart?.items) {
        this.apply(this.cart, root);
        return Promise.resolve(this.cart);
      }
      return this.refresh();
    },
    observe() {
      if (this.observer || !document.body) return;
      const selector = 'input[data-quantity-variant-id], cart-quantity-sync, [data-kkm-search-row]';
      this.observer = new MutationObserver((mutations) => {
        if (!this.cart?.items) return;
        mutations.forEach((mutation) => {
          mutation.addedNodes.forEach((node) => {
            if (!(node instanceof Element)) return;
            if (node.matches(selector) || node.querySelector(selector)) this.apply(this.cart, node);
          });
        });
      });
      this.observer.observe(document.body, { childList: true, subtree: true });
    },
    refresh() {
      if (this.pending) return this.pending;
      this.pending = fetch(`${routes.cart_url}.js`, { headers: { Accept: 'application/json' } })
        .then((response) => {
          if (!response.ok) throw new Error('Unable to refresh cart');
          return response.json();
        })
        .then((cart) => {
          this.apply(cart);
          return cart;
        })
        .finally(() => {
          this.pending = null;
        });
      return this.pending;
    },
  };

  window.KKMCartQuantityCoordinator = KKMCartQuantityCoordinator;

  const startKkmCartQuantityCoordinator = () => {
    KKMCartQuantityCoordinator.observe();
    KKMCartQuantityCoordinator.refresh().catch((error) => console.error(error));
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', startKkmCartQuantityCoordinator, { once: true });
  } else {
    startKkmCartQuantityCoordinator();
  }

  if (!window.KKMCartQuantityGlobalUnsubscriber && typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
    window.KKMCartQuantityGlobalUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
      if (event?.cartData?.items) {
        KKMCartQuantityCoordinator.apply(event.cartData);
      } else {
        KKMCartQuantityCoordinator.refresh().catch((error) => console.error(error));
      }
    });
  }

  customElements.define(
    'cart-quantity-sync',
    class CartQuantitySync extends HTMLElement {
      constructor() {
        super();
        this.input = this.querySelector('.quantity__input');
        this.onChange = this.onChange.bind(this);
      }

      connectedCallback() {
        if (!this.input) return;
        this.input.addEventListener('change', this.onChange);
        this.variantChangeUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, () => {
          queueMicrotask(() => {
            const quantity = parseInt(this.input?.dataset.cartQuantity || this.input?.value || 0);
            this.syncSubmitButton(quantity);
          });
        });
        this.syncSubmitButton(parseInt(this.input.dataset.cartQuantity || this.input.value || 0));
      }

      disconnectedCallback() {
        this.input?.removeEventListener('change', this.onChange);
        this.variantChangeUnsubscriber?.();
      }

      get variantId() {
        return this.input?.dataset.quantityVariantId;
      }

      normalizeQuantity(quantity) {
        const min = parseInt(this.input.dataset.min || '1');
        const step = parseInt(this.input.step || '1');
        const max = this.input.dataset.max ? parseInt(this.input.dataset.max) : null;

        if (!Number.isFinite(quantity) || quantity <= 0) return 0;
        let normalized = Math.max(quantity, min);
        if (normalized > min && step > 1) {
          normalized = min + Math.ceil((normalized - min) / step) * step;
        }
        if (max !== null) normalized = Math.min(normalized, max);
        return normalized;
      }

      setBusy(busy) {
        this.querySelectorAll('button').forEach((button) => {
          button.disabled = busy;
        });
      }

      syncSubmitButton(quantity) {
        const scope = this.closest('product-info');
        const submit = scope?.querySelector('product-form .product-form__submit[name="add"]');
        if (submit) submit.classList.toggle('hidden', quantity > 0);
      }

      syncFromCart(cart) {
        if (!this.input || !this.variantId) return;
        const quantity = KKMCartQuantityCoordinator.quantityFor(cart, this.variantId);
        this.input.value = quantity;
        this.input.setAttribute('value', quantity);
        this.input.dataset.cartQuantity = quantity;
        this.syncSubmitButton(quantity);
        publish(PUB_SUB_EVENTS.quantityUpdate, undefined);
      }

      async onChange() {
        if (!this.input || !this.variantId) return;

        const quantity = this.normalizeQuantity(parseInt(this.input.value || '0'));
        this.input.value = quantity;
        this.setBusy(true);

        try {
          const body = JSON.stringify({
            updates: { [this.variantId]: quantity },
            sections: ['cart-drawer', 'cart-icon-bubble', 'cart-icon-quick'],
            sections_url: window.location.pathname,
          });

          const response = await fetch(routes.cart_update_url, { ...fetchConfig(), body });
          const cart = await response.json();

          if (!response.ok || cart.errors || cart.status) {
            throw new Error(cart.description || cart.errors || window.cartStrings?.error || 'Unable to update cart');
          }

          const cartDrawer = document.querySelector('cart-drawer');
          if (cart.sections && cartDrawer?.renderContents) {
            cartDrawer.renderContents(cart, false);
          }

          KKMCartQuantityCoordinator.apply(cart);
          publish(PUB_SUB_EVENTS.cartUpdate, {
            source: 'kkm-cart-quantity-sync',
            cartData: cart,
            variantId: this.variantId,
          });
        } catch (error) {
          console.error(error);
          await KKMCartQuantityCoordinator.refresh().catch((refreshError) => console.error(refreshError));
        } finally {
          this.setBusy(false);
        }
      }
    }
  );
}